import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Grid, Button, IconButton,
    Divider, Paper, createTheme, ThemeProvider, CssBaseline
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import cart from '../Cart';
import User from '../data/User';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';

const defaultTheme = createTheme({
    palette: {
        primary: { main: '#e88ba9' },
        background: { default: '#fffafb' },
        text: { primary: '#5c434a', secondary: '#8c7077' },
    },
    typography: {
        fontFamily: '"Outfit", "Inter", sans-serif',
        h3: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 800 },
        h4: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 700 },
        h5: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 600 },
    },
});

const CheckoutButton = styled(Button)({
    background: '#e88ba9',
    borderRadius: '30px',
    color: 'white',
    padding: '12px 24px',
    boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1.1rem',
    width: '100%',
    transition: 'all 0.3s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)', background: '#d46a89' },
});

const QtyButton = styled(IconButton)({
    backgroundColor: '#fffafb',
    border: '1px solid #fcf4f4',
    color: '#d46a89',
    padding: '4px',
    '&:hover': { backgroundColor: '#fcf4f4' }
});

const CartItemsContainer = styled(Paper)({
    borderRadius: '24px',
    border: '1px solid #fcf4f4',
    boxShadow: '0 10px 30px rgba(232, 139, 169, 0.08)',
    padding: '32px',
    background: '#fff'
});

const SummaryContainer = styled(Paper)({
    borderRadius: '24px',
    border: '1px solid #e88ba9',
    boxShadow: '0 10px 40px rgba(232, 139, 169, 0.15)',
    padding: '32px',
    background: 'linear-gradient(135deg, #fffafb 0%, #ffedf4 100%)',
    position: 'sticky',
    top: '120px'
});

const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    const match = priceStr.toString().replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : 0;
};

const Cart = () => {
    const [items, setItems] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        setItems(cart.getItems());
    }, []);

    const handleRemove = (cakeId) => {
        cart.removeCake(cakeId);
        setItems(cart.getItems());
    };

    const handleQuantityChange = (cakeName, newQty) => {
        if (newQty <= 0) return;
        cart.changeCakeQuantity(cakeName, newQty);
        setItems([...cart.getItems()]);
    };

    const cartTotal = items.reduce((total, item) => total + (parsePrice(item.price) * item.quantity), 0);

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box sx={{ background: '#fffafb', minHeight: '100vh', pt: '100px', pb: 8 }}>
                <CssBaseline />
                <Container maxWidth="lg">

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                        <ShoppingBagOutlinedIcon sx={{ fontSize: 40, color: '#e88ba9' }} />
                        <Typography variant="h3" component="h1">
                            Your Shopping Bag
                        </Typography>
                    </Box>

                    {items.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 10 }}>
                            <img src="/logo.png" alt="Empty Cart" style={{ width: '150px', opacity: 0.5, marginBottom: '20px' }} />
                            <Typography variant="h5" sx={{ color: '#8c7077', mb: 3 }}>
                                Your bag is feeling a little light!
                            </Typography>
                            <Button variant="outlined" sx={{ borderRadius: '30px', borderColor: '#e88ba9', color: '#e88ba9' }} onClick={() => navigate('/menu')}>
                                Browse the Menu
                            </Button>
                        </Box>
                    ) : (
                        <Grid container spacing={4}>
                            {/* Left Column: Items */}
                            <Grid item xs={12} md={7} lg={8}>
                                <CartItemsContainer elevation={0}>
                                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'space-between', mb: 3 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#8c7077', textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '1px' }}>Product</Typography>
                                        <Typography sx={{ fontWeight: 700, color: '#8c7077', textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '1px' }}>Total</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 3, borderColor: '#fcf4f4' }} />

                                    {items.map((item, index) => {
                                        const itemPrice = parsePrice(item.price);
                                        const lineTotal = itemPrice * item.quantity;

                                        return (
                                            <React.Fragment key={index}>
                                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'center' }, gap: 3, mb: 3, mt: 3, textAlign: { xs: 'center', sm: 'left' } }}>
                                                    {/* Product Image */}
                                                    <Box
                                                        component="img"
                                                        src={item.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=200'}
                                                        sx={{ width: 100, height: 100, borderRadius: '16px', objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
                                                    />

                                                    {/* Details & Quantity */}
                                                    <Box sx={{ flexGrow: 1, width: '100%' }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#5c434a', mb: 0.5, lineHeight: 1.2 }}>
                                                            {item.name}
                                                        </Typography>
                                                        <Typography sx={{ color: '#d46a89', fontWeight: 600, fontSize: '0.9rem', mb: 2 }}>
                                                            LKR.{itemPrice.toFixed(2)}
                                                        </Typography>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 2, flexWrap: 'wrap' }}>
                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', background: '#fffafb', borderRadius: '20px', p: 0.5, border: '1px solid #fcf4f4' }}>
                                                                <QtyButton size="small" onClick={() => handleQuantityChange(item.name, item.quantity - 1)}>
                                                                    <RemoveIcon fontSize="small" />
                                                                </QtyButton>
                                                                <Typography sx={{ px: 2, fontWeight: 700, color: '#5c434a', minWidth: '30px', textAlign: 'center' }}>
                                                                    {item.quantity}
                                                                </Typography>
                                                                <QtyButton size="small" onClick={() => handleQuantityChange(item.name, item.quantity + 1)}>
                                                                    <AddIcon fontSize="small" />
                                                                </QtyButton>
                                                            </Box>
                                                            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleRemove(item.name)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                                                Remove
                                                            </Button>
                                                        </Box>
                                                    </Box>

                                                    {/* Line Total */}
                                                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' }, minWidth: { sm: '100px' }, width: { xs: '100%', sm: 'auto' }, pt: { xs: 2, sm: 0 }, borderTop: { xs: '1px dashed #fcf4f4', sm: 'none' } }}>
                                                        <Typography sx={{ fontWeight: 800, color: '#5c434a', fontSize: '1.1rem' }}>
                                                            LKR.{lineTotal.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {index < items.length - 1 && <Divider sx={{ borderColor: '#fcf4f4' }} />}
                                            </React.Fragment>
                                        );
                                    })}
                                </CartItemsContainer>
                            </Grid>

                            {/* Right Column: Summary Box */}
                            <Grid item xs={12} md={5} lg={4}>
                                <SummaryContainer elevation={0}>
                                    <Typography variant="h4" sx={{ mb: 4 }}>
                                        Order Summary
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography sx={{ color: '#8c7077', fontWeight: 500 }}>Subtotal</Typography>
                                        <Typography sx={{ color: '#5c434a', fontWeight: 700 }}>LKR.{cartTotal.toFixed(2)}</Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Typography sx={{ color: '#8c7077', fontWeight: 500 }}>Delivery</Typography>
                                        <Typography sx={{ color: '#8c7077', fontStyle: 'italic', fontSize: '0.9rem' }}>Calculated at next step</Typography>
                                    </Box>

                                    <Divider sx={{ borderColor: 'rgba(232, 139, 169, 0.2)', mb: 3 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 4 }}>
                                        <Typography variant="h5" sx={{ color: '#5c434a' }}>Total</Typography>
                                        <Typography variant="h4" sx={{ color: '#d46a89' }}>LKR.{cartTotal.toFixed(2)}</Typography>
                                    </Box>

                                    <CheckoutButton onClick={() => {
                                        if (User.instance.getToken()) {
                                            navigate('/checkout');
                                        } else {
                                            navigate('/login?redirect=checkout');
                                        }
                                    }}>
                                        Proceed to Checkout
                                    </CheckoutButton>
                                </SummaryContainer>
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default Cart;
